import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MessageCircle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SETTORI, PLAN_OPTIONS, whatsappLink, WA } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  nome_cognome: "", attivita: "", settore: "", citta: "",
  telefono: "", email: "", instagram: "", piano_interesse: "", obiettivi: "",
};

export default function ContactSection() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["nome_cognome", "attivita", "settore", "citta", "telefono", "email", "piano_interesse"];
    for (const r of required) {
      if (!form[r]) { toast.error("Compila tutti i campi obbligatori."); return; }
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Grazie! Fernando ti ricontatterà per conoscere meglio la tua attività.");
      setForm(empty);
    } catch (err) {
      toast.error("Errore nell'invio. Riprova o scrivici su WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "bg-[#101010] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-teal-light";

  return (
    <section id="contatti" data-testid="contact-section" className="relative py-24 lg:py-32">
      <div className="absolute inset-x-0 top-0 h-64 teal-glow pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-tight">
            La tua attività merita contenuti all'altezza del suo valore.
          </h2>
          <p className="mt-5 text-slate-300 leading-relaxed">
            Non lasciare che il tuo profilo diventi una semplice sequenza di fotografie, offerte e
            contenuti improvvisati. Costruisci una presenza social professionale, autentica e
            riconoscibile. Fernando e Fast Life Studio Agency penseranno alle idee, agli script,
            alle riprese, al montaggio e alla promozione.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3">
            <a
              data-testid="contact-whatsapp-cta"
              href={whatsappLink(WA.fernando)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-white hover:brightness-110 transition-all"
            >
              <MessageCircle size={18} /> Parla con Fernando
            </a>
            <button
              data-testid="contact-piani-cta"
              onClick={() => document.querySelector("#piani")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
            >
              Scegli il tuo piano
            </button>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-3 text-sm">
            <div className="rounded-xl border border-white/10 bg-[#0b0b0b] px-5 py-4 text-left">
              <p className="text-white font-medium">Fast 8 — 499 € al mese</p>
              <p className="text-slate-500 line-through">invece di 599 €</p>
            </div>
            <div className="rounded-xl border border-teal-light/30 bg-[#0c1413] px-5 py-4 text-left">
              <p className="text-white font-medium">Fast 12 — 699 € al mese</p>
              <p className="text-slate-500 line-through">invece di 799 €</p>
            </div>
          </div>
          <p className="mt-4 text-sm text-teal-light">100 € di sconto su entrambi i piani · Sponsorizzazioni già incluse · Nessun costo nascosto</p>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={submit}
          className="mt-12 rounded-3xl border border-white/10 bg-[#0b0b0b] p-6 lg:p-10 grid sm:grid-cols-2 gap-5"
        >
          <Field label="Nome e cognome *"><Input data-testid="input-nome" className={inputCls} value={form.nome_cognome} onChange={set("nome_cognome")} placeholder="Mario Rossi" /></Field>
          <Field label="Nome dell'attività *"><Input data-testid="input-attivita" className={inputCls} value={form.attivita} onChange={set("attivita")} placeholder="La tua azienda" /></Field>

          <Field label="Settore *">
            <Select value={form.settore} onValueChange={set("settore")}>
              <SelectTrigger data-testid="select-settore" className={inputCls}><SelectValue placeholder="Seleziona settore" /></SelectTrigger>
              <SelectContent className="bg-[#101010] border-white/10 text-white">
                {SETTORI.map((s) => <SelectItem key={s} value={s} data-testid={`settore-${s}`}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Città *"><Input data-testid="input-citta" className={inputCls} value={form.citta} onChange={set("citta")} placeholder="La tua città" /></Field>

          <Field label="Numero di telefono *"><Input data-testid="input-telefono" className={inputCls} value={form.telefono} onChange={set("telefono")} placeholder="+39 ..." /></Field>
          <Field label="E-mail *"><Input data-testid="input-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="nome@email.it" /></Field>

          <Field label="Profilo Instagram"><Input data-testid="input-instagram" className={inputCls} value={form.instagram} onChange={set("instagram")} placeholder="@iltuoprofilo" /></Field>
          <Field label="Piano di interesse *">
            <Select value={form.piano_interesse} onValueChange={set("piano_interesse")}>
              <SelectTrigger data-testid="select-piano" className={inputCls}><SelectValue placeholder="Seleziona un'opzione" /></SelectTrigger>
              <SelectContent className="bg-[#101010] border-white/10 text-white">
                {PLAN_OPTIONS.map((p, i) => <SelectItem key={p.value} value={p.value} data-testid={`piano-opt-${i}`}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          <div className="sm:col-span-2">
            <Field label="Breve messaggio">
              <Textarea data-testid="input-obiettivi" className={inputCls} rows={4} value={form.obiettivi} onChange={set("obiettivi")} placeholder="Raccontaci brevemente la tua attività e i tuoi obiettivi..." />
            </Field>
          </div>

          <div className="sm:col-span-2">
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-teal-light py-4 text-sm font-semibold text-[#050505] hover:bg-white transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              {loading ? "Invio in corso..." : "Richiedi una consulenza"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-2">
      <Label className="text-xs uppercase tracking-wider text-slate-400">{label}</Label>
      {children}
    </div>
  );
}
